import { useState, useEffect, useCallback } from 'react';
import {
  Map, RefreshCw, CheckCircle2, AlertTriangle, XCircle,
  Globe, FileText, Search, BarChart3, Shield
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/lib/api';

const scoreColor = (s) => s >= 90 ? 'text-emerald-400' : s >= 70 ? 'text-amber-400' : 'text-red-400';
const scoreBg = (s) => s >= 90 ? 'bg-emerald-500/15 border-emerald-500/30' : s >= 70 ? 'bg-amber-500/15 border-amber-500/30' : 'bg-red-500/15 border-red-500/30';

export default function SitemapHealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const loadHealth = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/sitemap/health');
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHealth(); }, [loadHealth]);

  const runScan = async () => {
    setScanning(true);
    toast.info('Tum sitemap\'ler kontrol ediliyor...');
    try {
      const res = await api.get('/admin/sitemap/health');
      setData(res.data);
      toast.success(`${res.data.total} site kontrol edildi. Ort: ${res.data.avg_health}/100`);
    } catch (err) {
      toast.error('Hata: ' + (err.response?.data?.detail || err.message));
    } finally {
      setScanning(false);
    }
  };

  const sites = data?.sites || [];
  const avg = data?.avg_health || 0;

  return (
    <div data-testid="sitemap-health-page" className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 data-testid="sitemap-health-title" className="text-2xl sm:text-3xl font-semibold tracking-tight font-['Space_Grotesk']">
            Sitemap Intelligence
          </h1>
          <p className="text-sm text-muted-foreground mt-1">XML validation, health scoring, self-healing</p>
        </div>
        <Button onClick={runScan} disabled={scanning} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
          {scanning ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
          {scanning ? 'Taraniyor...' : 'Tum Siteleri Tara'}
        </Button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card className="bg-[hsl(var(--card))] border-border/70 p-4">
          <p className="text-[11px] text-muted-foreground uppercase">Toplam Site</p>
          <p className="text-2xl font-semibold mt-1">{data?.total || 0}</p>
        </Card>
        <Card className="bg-[hsl(var(--card))] border-border/70 p-4">
          <p className="text-[11px] text-muted-foreground uppercase">Ort. Skor</p>
          <p className={`text-2xl font-semibold mt-1 ${scoreColor(avg)}`}>{avg}</p>
        </Card>
        <Card className="bg-[hsl(var(--card))] border-border/70 p-4">
          <p className="text-[11px] text-muted-foreground uppercase">Saglam</p>
          <p className="text-2xl font-semibold mt-1 text-emerald-400">{data?.ok || 0}</p>
        </Card>
        <Card className="bg-[hsl(var(--card))] border-border/70 p-4">
          <p className="text-[11px] text-muted-foreground uppercase">Uyari</p>
          <p className="text-2xl font-semibold mt-1 text-amber-400">{data?.warning || 0}</p>
        </Card>
        <Card className="bg-[hsl(var(--card))] border-border/70 p-4">
          <p className="text-[11px] text-muted-foreground uppercase">Kritik</p>
          <p className="text-2xl font-semibold mt-1 text-red-400">{data?.critical || 0}</p>
        </Card>
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}</div>
      ) : (
        <Card className="bg-[hsl(var(--card))] border-border/70 overflow-hidden">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="text-xs text-muted-foreground">Domain</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Template</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Skor</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Sayfalar</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Sitemap URL</TableHead>
                  <TableHead className="text-xs text-muted-foreground">News</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Sorunlar</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Kontrol</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sites.map(s => (
                  <TableRow key={s.site_id} className="border-border/30 hover:bg-secondary/20">
                    <TableCell className="font-mono text-xs">{s.domain}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] border-border/50">
                        {s.template === 'media_affiliate_v3' ? 'V3' : s.template === 'entry_match_scores' ? 'V2' : 'V1'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center justify-center w-10 h-7 rounded-md text-xs font-bold border ${scoreBg(s.sitemap_health)} ${scoreColor(s.sitemap_health)}`}>
                        {s.sitemap_health}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">{s.page_count}</TableCell>
                    <TableCell className="text-xs">{s.sitemap_url_count}</TableCell>
                    <TableCell className="text-xs">{s.news_url_count || '-'}</TableCell>
                    <TableCell className="text-xs">
                      {(s.sitemap_issues || []).length === 0
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        : <span className="text-amber-400">{s.sitemap_issues.length}</span>
                      }
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {s.checked_at ? new Date(s.checked_at).toLocaleString('tr-TR', {hour:'2-digit',minute:'2-digit'}) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </Card>
      )}
    </div>
  );
}
